import { useCallback, useEffect, useRef, useState } from "react";
import config from "src/common/config";
import { getGlobalHeaders } from "src/common/relay-env";

import {
  isSupportedAttachment,
  MAX_ATTACHMENT_SIZE,
  MAX_ATTACHMENTS_PER_MESSAGE,
} from "./attachment-constants";

export type ChatAttachmentStatusType = "uploading" | "ready" | "error";

export interface ChatAttachmentType {
  /** Stable client-side id (also used as React key). */
  id: string;
  file: File;
  status: ChatAttachmentStatusType;
  /** Object URL for image preview thumbnails (revoked on remove/unmount). */
  previewUrl?: string;
  /** Set once the upload completes — opaque, signed handle the server hands back. */
  handle?: string;
  errorMessage?: string;
}

interface UseChatAttachmentsType {
  attachments: ChatAttachmentType[];
  /** True while any attachment is mid-upload — composer should disable send. */
  isUploading: boolean;
  /** True when no chips are in `error` state and at least one would block a clean send. */
  hasError: boolean;
  /**
   * Single entry point for every file the composer receives — accepted drops, picker
   * selections, pastes, AND dropzone-rejected files (passed with a preset error so they
   * surface as error chips instead of vanishing). One slot-budget spend per call.
   */
  addFiles: (files: File[] | FileList, presetErrors?: Map<File, string>) => void;
  removeAttachment: (id: string) => void;
  /** Clear all chips after a successful submit. */
  clearAll: () => void;
}

const generateId = (): string => Math.random().toString(36).slice(2);

const formatBytesError = (limit: number): string => {
  const mb = Math.round(limit / 1024 / 1024);
  return `File exceeds ${mb}MB limit`;
};

/** Manages composer attachment state — validation, per-file uploads, abort/cleanup. */
export const useChatAttachments = (): UseChatAttachmentsType => {
  const [attachments, setAttachments] = useState<ChatAttachmentType[]>([]);
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Revoke any outstanding object URLs on unmount so we don't leak memory.
  useEffect(() => {
    return () => {
      setAttachments((prev) => {
        prev.forEach((a) => {
          if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
        });
        return prev;
      });
      abortControllersRef.current.forEach((c) => c.abort());
      abortControllersRef.current.clear();
    };
  }, []);

  const uploadFile = useCallback(async (attachment: ChatAttachmentType): Promise<void> => {
    const controller = new AbortController();
    abortControllersRef.current.set(attachment.id, controller);

    const formData = new FormData();
    formData.append("file", attachment.file, attachment.file.name);

    try {
      const headers = { ...getGlobalHeaders() };
      // Strip Content-Type so fetch sets the multipart boundary itself.
      delete headers["Content-Type"];
      const orgId = localStorage.getItem("tz_current_org_id") || "default";
      const url = `${config.get("APP_WWW_URL")}/internal/chat/attachments`;
      // eslint-disable-next-line custom/no-hardcoded-brand-name -- HTTP header name matches backend
      headers["X-TierZero-Org-Id"] = orgId;

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: formData,
        signal: controller.signal,
      });

      if (!response.ok) {
        let detail = `Upload failed (${response.status})`;
        try {
          const body = await response.json();
          if (body?.detail) detail = body.detail;
        } catch {
          // Non-JSON body — keep the generic message.
        }
        setAttachments((prev) =>
          prev.map((a) => (a.id === attachment.id ? { ...a, status: "error", errorMessage: detail } : a))
        );
        return;
      }

      const body = (await response.json()) as { handle: string };
      setAttachments((prev) =>
        prev.map((a) => (a.id === attachment.id ? { ...a, status: "ready", handle: body.handle } : a))
      );
    } catch (e) {
      if ((e as Error).name === "AbortError") return;
      setAttachments((prev) =>
        prev.map((a) =>
          a.id === attachment.id ? { ...a, status: "error", errorMessage: (e as Error).message } : a
        )
      );
    } finally {
      abortControllersRef.current.delete(attachment.id);
    }
  }, []);

  const addFiles = useCallback(
    (input: File[] | FileList, presetErrors?: Map<File, string>) => {
      const incoming = Array.from(input);
      setAttachments((prev) => {
        // Spend the slot budget exactly once for the whole batch: files beyond the
        // remaining slots become visible "limit reached" error chips, never a silent drop.
        const remainingSlots = Math.max(0, MAX_ATTACHMENTS_PER_MESSAGE - prev.length);
        const newAttachments: ChatAttachmentType[] = incoming.map((file, index) => {
          const id = generateId();
          const isImage = file.type.startsWith("image/");
          const previewUrl = isImage ? URL.createObjectURL(file) : undefined;

          if (index >= remainingSlots) {
            return {
              id,
              file,
              previewUrl,
              status: "error",
              errorMessage: `Attachment limit reached (max ${MAX_ATTACHMENTS_PER_MESSAGE})`,
            };
          }
          // A dropzone-level rejection wins over content validation — the file was
          // never accepted, so it must surface as an error chip and never upload.
          const presetError = presetErrors?.get(file);
          if (presetError) {
            return { id, file, previewUrl, status: "error", errorMessage: presetError };
          }
          if (file.size === 0) {
            return { id, file, previewUrl, status: "error", errorMessage: "Empty file" };
          }
          if (file.size > MAX_ATTACHMENT_SIZE) {
            return { id, file, previewUrl, status: "error", errorMessage: formatBytesError(MAX_ATTACHMENT_SIZE) };
          }
          if (!isSupportedAttachment(file.type, file.name)) {
            return { id, file, previewUrl, status: "error", errorMessage: "Unsupported file type" };
          }
          return { id, file, previewUrl, status: "uploading" };
        });

        // Kick off uploads outside the state updater.
        queueMicrotask(() => {
          newAttachments.forEach((a) => {
            if (a.status === "uploading") {
              void uploadFile(a);
            }
          });
        });

        return [...prev, ...newAttachments];
      });
    },
    [uploadFile]
  );

  const removeAttachment = useCallback((id: string) => {
    const controller = abortControllersRef.current.get(id);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(id);
    }
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const clearAll = useCallback(() => {
    abortControllersRef.current.forEach((c) => c.abort());
    abortControllersRef.current.clear();
    setAttachments((prev) => {
      prev.forEach((a) => {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      });
      return [];
    });
  }, []);

  const isUploading = attachments.some((a) => a.status === "uploading");
  const hasError = attachments.some((a) => a.status === "error");

  return { attachments, isUploading, hasError, addFiles, removeAttachment, clearAll };
};
